import React, { useRef, useState } from 'react';

import { Button } from 'react-covideo-common';
import { IoPersonCircle } from 'react-icons/io5';
import { MdLaptop, MdPerson, MdSettings } from 'react-icons/md';
import styled, { useTheme } from 'styled-components/macro';
import {
  enableCamera,
  getSafariScreenAndCameraStreams,
  getScreenAndCameraStreams,
  handleScreenCapture,
} from './utils/media';
import { ModalRecordingVideosSettings } from './ModalRecordingVideosSettings';
import { RecordBoth } from './RecordBoth';
import { RecordCam } from './RecordCam';
import { ScreenRecorder } from './ScreenRecorder';
import { isOpera, isFirefox, isSafari } from '../../utils/browserCheck';
import { getAudioSettings, getVideoSettings } from './utils/recorderSettings';
import { useRecordSettings } from '../../context/record/provider';
import {
  Heading,
  ParagraphNormal,
  ParagraphSmallBold,
} from '../../components/typography';
import { Gap, Stack } from '../../components/layout';

import { useParams } from 'react-router';
import { useVideoReplyQuery } from 'queries/videoReply/useVideoReplyQuery';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { Trans, useTranslation } from 'react-i18next';

const RecordOption = styled.div<{ error: boolean }>`
  display: flex;
  flex-direction: column;
  width: 108px;
  height: 108px;
  padding: 32px;
  border-radius: 4px;
  border: solid 2px #eeeff2;
  background-color: white;
  justify-content: space-between;
  align-items: center;
  color: ${({ theme }) => theme.colors.neutral[80]};

  &:hover {
    cursor: ${({ error }) => (error ? 'not-allowed' : 'pointer')};
    border: solid 2px #d0d3d9;
  }
`;

const MainRecordContentWrapper = styled(Gap)`
  width: 592px;
  padding: 32px 104px 51px;
  border-radius: 8px;
  margin: 56px auto 0 auto;
  background-color: ${({ theme }) => theme.colors.neutral[10]};
  @media screen and (max-width: 768px) {
    width: 100%;
    padding: 24px 0;
  }
`;

const RecordingOptionWrapper = styled(Gap)`
  @media screen and (max-width: 768px) {
    flex-direction: column;
    .hide-on-mobile {
      display: none;
    }
  }
`;

const ShowRecorderWrapper = styled.div`
  width: 770px;
  @media screen and (max-width: 768px) {
    width: 100%;
  }
`;

const IoPersonCircleWrapper = styled.div`
  background-color: white;
  border-radius: 50px;
  position: absolute;
  border: 2px solid white;
  bottom: 65px;
  left: 35px;
`;

export const ChooseRecordingOptions = ({
  extendStyles,
  showPreview,
  onStartUpladingVoiceOverHandler,
}: {
  extendStyles?: {
    mainRecordContentWrapper?: React.CSSProperties;
    showRecorderWrapper?: React.CSSProperties;
  };
  showPreview?: boolean;
  onStartUpladingVoiceOverHandler?: (blob: Blob) => void;
}) => {
  const { t } = useTranslation();
  const { shortUrl, eridEncoded } = useParams() as {
    shortUrl: string;
    eridEncoded: string;
  };

  const { data, isError, isLoading } = useVideoReplyQuery({
    shortUrl,
    eridEncoded,
  });

  const { userSettings, recordingError, updateErrorMessage } =
    useRecordSettings();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { colors } = useTheme();
  const [showRecordOption, setShowRecordOption] = useState<
    'cam' | 'screen' | 'both' | null
  >(null);
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  // main stream
  const [screenStream, setScreenStream] = useState<MediaStream | null>(null);
  // helpers for cleaning streams
  const displayMediaRef = useRef<MediaStream | null>(null);
  const cameraStreamRef = useRef<MediaStream | null>(null);
  // PIP ref
  const videoCameraPipRef = useRef<HTMLVideoElement>(null);

  const audioSettings = getAudioSettings(userSettings);
  const videoSettings = getVideoSettings(userSettings);

  const stopAllStreams = async () => {
    if (displayMediaRef.current) {
      displayMediaRef.current.getTracks().forEach(track => track.stop());
    }
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach(track => track.stop());
      cameraStreamRef.current = null;
    }
    if (screenStream) {
      screenStream.getTracks().forEach(track => track.stop());
    }
    if (document.pictureInPictureElement) {
      await document.exitPictureInPicture();
    }
    if (videoCameraPipRef.current && videoCameraPipRef.current.srcObject) {
      const stream = videoCameraPipRef.current.srcObject as MediaStream;

      stream.getTracks().forEach(track => track.stop());

      videoCameraPipRef.current.srcObject = null;
    }
  };

  const screenCaptureHandler = async () => {
    if (recordingError || isPromptOpen) {
      return;
    }
    try {
      setIsPromptOpen(true);
      const stream = await handleScreenCapture(
        displayMediaRef,
        audioSettings,
        videoSettings
      );
      if (stream) {
        setScreenStream(stream);
        setShowRecordOption('screen');
      }
      setIsPromptOpen(false);
    } catch (error) {
      stopAllStreams();
      setIsPromptOpen(false);
      console.log(error, 'screenCaptureHandler');
    }
  };
  const enableCameraHandler = async () => {
    if (recordingError || isPromptOpen) {
      return;
    }
    try {
      setIsPromptOpen(true);
      const stream = await enableCamera(audioSettings, videoSettings);
      if (stream) {
        setScreenStream(stream);
        setShowRecordOption('cam');
      }
      setIsPromptOpen(false);
    } catch (error) {
      stopAllStreams();
      setIsPromptOpen(false);
      console.log(error, 'enableCameraHandler');
      updateErrorMessage();
    }
  };
  const enableBothHandler = async () => {
    if (recordingError || videoCameraPipRef.current === null || isPromptOpen) {
      return;
    }

    try {
      setIsPromptOpen(true);
      const stream = !isSafari
        ? await getScreenAndCameraStreams(
            displayMediaRef,
            cameraStreamRef,
            audioSettings,
            videoSettings,
            videoCameraPipRef
          )
        : await getSafariScreenAndCameraStreams(
            displayMediaRef,
            cameraStreamRef,
            audioSettings,
            videoSettings,
            videoCameraPipRef
          );

      setScreenStream(stream);
      setShowRecordOption('both');
      setIsPromptOpen(false);
    } catch (error) {
      stopAllStreams();
      console.log(error, 'enableCameraHandler');
      setIsPromptOpen(false);
    }
  };

  const handleGoToRecordHome = () => {
    setShowRecordOption(null);
    stopAllStreams();
  };
  if (isLoading) {
    return (
      <LoadingIndicator
        isLoading={true}
        text={t('Loading...')}
      ></LoadingIndicator>
    );
  }

  if (!shortUrl || !eridEncoded || isError) {
    return (
      <Gap
        gap='32px'
        style={{
          minHeight: '100vh',
          marginTop: '-50px',
          justifyContent: 'center',
        }}
      >
        <ParagraphSmallBold color={colors.danger[100]} textAlign='center'>
          <Trans i18nKey='videoReplyLoadError' components={{ br: <br /> }} />
        </ParagraphSmallBold>
      </Gap>
    );
  }

  return (
    <>
      <Stack
        gap='32px'
        style={{
          minHeight: '100vh',
          marginTop: '-50px',
          justifyContent: 'center',
        }}
      >
        {showRecordOption === null && (
          <MainRecordContentWrapper
            flexDirection='column'
            gap='44px'
            style={extendStyles?.mainRecordContentWrapper}
          >
            <Heading color={colors.black[100]}>
              {t('Choose recording option')}
            </Heading>
            <RecordingOptionWrapper gap='32px'>
              {!isOpera && !isFirefox && (
                <RecordOption
                  onClick={enableBothHandler}
                  style={{ position: 'relative' }}
                  error={!!recordingError}
                  className='hide-on-mobile'
                >
                  <MdLaptop size={72} />
                  <IoPersonCircleWrapper>
                    <IoPersonCircle size={28} />
                  </IoPersonCircleWrapper>
                  <ParagraphNormal textAlign='center' color={colors.black[100]}>
                    {t('Screen+Cam')}
                  </ParagraphNormal>
                </RecordOption>
              )}
              <RecordOption
                onClick={screenCaptureHandler}
                error={!!recordingError}
                className='hide-on-mobile'
              >
                <MdLaptop size={72} />
                <ParagraphNormal textAlign='center' color={colors.black[100]}>
                  {t('Screen Only')}
                </ParagraphNormal>
              </RecordOption>
              <RecordOption
                onClick={enableCameraHandler}
                error={!!recordingError}
              >
                <MdPerson size={72} />
                <ParagraphNormal textAlign='center' color={colors.black[100]}>
                  {t('Cam Only')}
                </ParagraphNormal>
              </RecordOption>
            </RecordingOptionWrapper>
          </MainRecordContentWrapper>
        )}
        {!!recordingError && showRecordOption === null && (
          <ParagraphSmallBold>{recordingError}</ParagraphSmallBold>
        )}

        {showRecordOption === 'screen' &&
          screenStream instanceof MediaStream && (
            <ShowRecorderWrapper style={extendStyles?.showRecorderWrapper}>
              <ScreenRecorder
                playerDesign={data?.playerDesign}
                screenStream={screenStream}
                stopAllStreams={stopAllStreams}
                handleGoToRecordHome={handleGoToRecordHome}
                showPreview={showPreview}
                onStartUpladingVoiceOverHandler={
                  onStartUpladingVoiceOverHandler
                }
              />
            </ShowRecorderWrapper>
          )}

        {showRecordOption === 'cam' && screenStream instanceof MediaStream && (
          <ShowRecorderWrapper style={extendStyles?.showRecorderWrapper}>
            <RecordCam
              playerDesign={data?.playerDesign}
              screenStream={screenStream}
              stopAllStreams={stopAllStreams}
              handleGoToRecordHome={handleGoToRecordHome}
              showPreview={showPreview}
              onStartUpladingVoiceOverHandler={onStartUpladingVoiceOverHandler}
            />
          </ShowRecorderWrapper>
        )}
        {showRecordOption === 'both' && screenStream instanceof MediaStream && (
          <ShowRecorderWrapper style={extendStyles?.showRecorderWrapper}>
            <RecordBoth
              playerDesign={data?.playerDesign}
              screenStream={screenStream}
              stopAllStreams={stopAllStreams}
              handleGoToRecordHome={handleGoToRecordHome}
              showPreview={showPreview}
              onStartUpladingVoiceOverHandler={onStartUpladingVoiceOverHandler}
            />
          </ShowRecorderWrapper>
        )}

        <video ref={videoCameraPipRef} style={{ display: 'none' }} />

        {showRecordOption === null && (
          <Button
            icon={<MdSettings size={18} />}
            variant='secondary'
            text={t('Recording settings')}
            onClick={() => setSettingsOpen(true)}
          />
        )}
      </Stack>
      {settingsOpen && (
        <ModalRecordingVideosSettings
          handleModalClose={() => setSettingsOpen(false)}
        />
      )}
    </>
  );
};
